/**
 * Square footage on Scanbrix scans / workspaces (used for order pricing).
 * Field names vary by app version — we probe common keys and nested paths.
 */

const DEFAULT_SQFT_FIELDS = [
  "area_sq_ft",
  "areaSqFt",
  "area_sq_m",
  "areaSqM",
  "sqft",
  "sq_ft",
  "square_feet",
  "squareFeet",
  "total_sqft",
  "totalSqft",
  "total_square_feet",
  "totalSquareFeet",
  "scan_sqft",
  "scanSqft",
  "processed_sqft",
  "processedSqft",
  "billable_sqft",
  "billableSqft",
  "order_sqft",
  "orderSqft",
  "square_footage",
  "squareFootage",
  "floor_area_sqft",
  "floorAreaSqft",
  "area_sqft",
  "areaSqft",
  "gross_sqft",
  "grossSqft",
  "net_sqft",
  "netSqft",
  "area",
  "total_area",
  "totalArea",
  "floor_area",
  "floorArea",
  "measured_area",
  "measuredArea",
  "gross_area",
  "grossArea",
  "net_area",
  "netArea",
  "size_sqft",
  "sizeSqft",
];

const DEFAULT_SQFT_DISPLAY_FIELDS = ["area_display", "areaDisplay"];

const DEFAULT_SQFT_PATHS = [
  "measurements.sqft",
  "measurements.square_feet",
  "measurements.total_sqft",
  "metrics.sqft",
  "metrics.square_feet",
  "processing.sqft",
  "processing.square_feet",
  "dimensions.square_feet",
  "dimensions.sqft",
  "area.sqft",
  "area.square_feet",
  "stats.sqft",
  "stats.square_feet",
];

const SQM_UNIT_TOKENS = new Set(["sqm", "m2", "m²", "square_meter", "square_meters", "squaremeter", "squaremeters"]);

function parseFieldListEnv(raw) {
  if (!raw || typeof raw !== "string") return null;
  const parts = raw
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  return parts.length ? parts : null;
}

function sqftFieldNames() {
  return parseFieldListEnv(process.env.REACT_APP_SCAN_SQFT_FIELDS) || DEFAULT_SQFT_FIELDS;
}

function sqftFieldPaths() {
  return parseFieldListEnv(process.env.REACT_APP_SCAN_SQFT_PATHS) || DEFAULT_SQFT_PATHS;
}

function getByPath(obj, path) {
  if (!obj || typeof path !== "string") return undefined;
  const parts = path.split(".");
  let cur = obj;
  for (const p of parts) {
    cur = cur?.[p];
  }
  return cur;
}

function parsePositiveNumber(raw) {
  if (raw == null || raw === "") return null;
  if (typeof raw === "number" && Number.isFinite(raw) && raw > 0) return raw;
  const s = String(raw).trim().replace(/,/g, "");
  if (!s) return null;
  const n = Number(s);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function parseAreaDisplayString(raw) {
  if (raw == null || raw === "") return null;
  const s = String(raw).trim();
  if (!s) return null;
  const sqftMatch = s.match(/([\d,]+(?:\.\d+)?)\s*(?:sq\s*\.?\s*ft|sqft|ft²|ft2)\b/i);
  if (sqftMatch) return parsePositiveNumber(sqftMatch[1]);
  const sqmMatch = s.match(/([\d,]+(?:\.\d+)?)\s*(?:sq\s*\.?\s*m|sqm|m²|m2)\b/i);
  if (sqmMatch) {
    const n = parsePositiveNumber(sqmMatch[1]);
    return n == null ? null : n * 10.763910416709722;
  }
  return null;
}

function fieldNameImpliesSqMeters(fieldName) {
  const key = String(fieldName || "").toLowerCase();
  return key.includes("sq_m") || key.includes("sqm") || /(?:^|_)m2$/.test(key) || key.endsWith("_m");
}

function toSqft(value, doc, fieldName = "") {
  const n = parsePositiveNumber(value);
  if (n == null) return null;
  if (fieldNameImpliesSqMeters(fieldName)) return n * 10.763910416709722;
  if (unitLooksLikeSqMeters(doc)) return n * 10.763910416709722;
  return n;
}

function unitLooksLikeSqMeters(doc) {
  const unitRaw =
    doc?.area_unit ??
    doc?.areaUnit ??
    doc?.unit ??
    doc?.measurement_unit ??
    doc?.measurementUnit ??
    doc?.dimensions?.unit ??
    doc?.measurements?.unit;
  if (unitRaw == null) return false;
  const u = String(unitRaw).trim().toLowerCase();
  return SQM_UNIT_TOKENS.has(u) || u.includes("meter") || u.includes("metre");
}

/** @returns {number|null} Square feet from a scan, file, or workspace Firestore doc. */
export function extractSqftFromDoc(doc) {
  if (!doc || typeof doc !== "object") return null;

  for (const key of sqftFieldNames()) {
    const v = doc[key];
    const n = toSqft(v, doc, key);
    if (n != null) return n;
  }

  for (const key of DEFAULT_SQFT_DISPLAY_FIELDS) {
    const n = parseAreaDisplayString(doc[key]);
    if (n != null) return n;
  }

  for (const path of sqftFieldPaths()) {
    const v = getByPath(doc, path);
    const leaf = path.split(".").pop() || path;
    const n = toSqft(v, doc, leaf);
    if (n != null) return n;
  }

  return null;
}

export function workspaceIdFromDoc(doc) {
  if (!doc || typeof doc !== "object") return "";
  const w =
    doc.workspace_id ??
    doc.workspaceId ??
    doc.workspaceID ??
    (doc.workspace && typeof doc.workspace === "object" && doc.workspace.id) ??
    (typeof doc.workspace === "string" ? doc.workspace : null);
  return w != null && String(w).trim() ? String(w).trim() : "";
}

export function scanLabelFromDoc(doc) {
  if (!doc || typeof doc !== "object") return "Scan";
  return (
    doc.scan_name ||
    doc.name ||
    doc.title ||
    doc.fileName ||
    doc.file_name ||
    (doc.id != null ? String(doc.id).slice(0, 10) : "Scan")
  );
}

export function formatSqft(value, { fallback = "—" } = {}) {
  if (value == null || !Number.isFinite(value) || value <= 0) return fallback;
  const rounded = value >= 100 ? Math.round(value) : Math.round(value * 10) / 10;
  return `${rounded.toLocaleString()} sq ft`;
}
